import { VolumeUpIcon } from "@heroicons/react/solid";
import { CheckCircledIcon } from "@radix-ui/react-icons";
import * as Sentry from "@sentry/nextjs";
import clsx from "clsx";
import * as dateFns from "date-fns";
import * as dateFnsTZ from "date-fns-tz";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import React, { useMemo, useState } from "react";
import useInView from "react-cool-inview";
import toast, { Toaster } from "react-hot-toast";
import ReactStars from "react-rating-stars-component";
import { useInterval } from "react-use";
import Layout from "~/components/AppLayout";
import { Avatar } from "~/components/avatar";
import { Footer } from "~/components/footer";
import { HeadContent } from "~/components/head";
import {
  fancyTimeFormat,
  getTimeDataFromSeconds,
} from "~/components/workout/helpers";
import {
  getWorkoutByID,
  useWorkout,
} from "~/components/workout/hooks/useWorkout";
import { isLocal, limit } from "~/helpers/misc";
import { getProgramCalanderByDateRange } from "~/hooks/useProgramCalendar";
import { useTeams } from "~/hooks/useTeams";
import { captureEvent } from "~/lib/captureEvent";
import { WO_EMAIL_CAPTURE_COOKIE_NAME } from "~/lib/constants";
import { identify } from "~/lib/identify";
import { fetcher, getUrlWithUtms } from "~/lib/misc";
import { ExperimentProvider } from "~/state-containers/experiment";

function Countdown({ enabled, size = "sm" }) {
  const [key, setKey] = useState(0);
  useInterval(() => enabled && setKey((n) => n + 1), 1000);

  const { hrs, mins, secs } = useMemo(() => {
    const diff = dateFns.differenceInSeconds(
      dateFns.startOfDay(dateFns.addDays(new Date(), 1)),
      new Date()
    );
    return getTimeDataFromSeconds(diff);
  }, [key]);

  if (!enabled) return null;

  return (
    <div className="flex space-x-2 text-center">
      <div className="flex items-end">
        <div
          className={clsx("-mb-1 tracking-widest center font-druk", {
            "text-2xl": size === "sm",
            "text-4xl": size === "lg",
          })}
        >
          {hrs}
        </div>
        <span className="text-xs">HR</span>
      </div>
      <div className="flex items-end">
        <div
          className={clsx("-mb-1 tracking-widest center font-druk", {
            "text-2xl": size === "sm",
            "text-4xl": size === "lg",
          })}
        >
          {mins}
        </div>
        <span className="text-xs">MIN</span>
      </div>
      <div className="flex items-end">
        <div
          className={clsx("-mb-1 tracking-widest center font-druk", {
            "text-2xl": size === "sm",
            "text-4xl": size === "lg",
          })}
        >
          {secs}
        </div>
        <span className="text-xs">SEC</span>
      </div>
    </div>
  );
}

const PlaylistItem = ({ data, activeIndex, ndx }) => {
  const {
    name,
    duration,
    movementThumbnailUrl,
    thumbnailUrl,
    audioUrl,
    muxAudioUrl,
    reps,
  } = data;

  const img = movementThumbnailUrl || thumbnailUrl;
  return (
    <div
      className={clsx(
        "flex relative justify-between w-full px-2 py-2 pr-2 text-left rounded-r-lg md:px-6 no-highlight"
      )}
    >
      {activeIndex === ndx ? (
        <div className="absolute top-0 bottom-0 left-0 w-1 h-full rounded bg-lemon" />
      ) : null}
      <div className="flex items-center pr-4 space-x-4">
        {img ? (
          <img
            className="self-start w-12 h-12 rounded-md 2xl:w-16 2xl:h-16"
            src={
              (movementThumbnailUrl ||
                thumbnailUrl ||
                `/assets/logo/white-icon.svg`) + "?w=80"
            }
          />
        ) : (
          <div className="rounded-md bg-neutral-900">
            <img
              className="self-start w-12 h-12 p-3 2xl:w-16 2xl:h-16"
              src={`/assets/logo/white-icon.svg`}
            />
          </div>
        )}
        <div className="flex flex-col">
          <span
            className={clsx("opacity-90", {
              "font-bold": activeIndex === ndx,
              "font-medium": activeIndex !== ndx,
            })}
          >
            {name}
          </span>
          <div className="flex items-center space-x-2 text-sm opacity-70">
            <span>{fancyTimeFormat(duration)}</span>
            {audioUrl || muxAudioUrl ? (
              <VolumeUpIcon className="w-3 h-3 text-white" />
            ) : null}
            {reps ? <span className="opacity-60">{`${reps} reps`}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const Playlist = ({ data }) => {
  return (
    <div className="py-4">
      {limit(data, 8).map(({ type, ...movement }, ndx) =>
        type !== "section" ? (
          <PlaylistItem
            key={movement.id}
            data={movement}
            activeIndex={0}
            ndx={ndx}
          />
        ) : null
      )}
    </div>
  );
};

const Card = React.memo(({ workout, teamID, countdown }) => {
  const { data } = useWorkout(workout.id);
  const { activeTeam: team } = useTeams(teamID);

  return (
    <div className="rounded-2xl shadow-xl max-w-md mx-auto border border-[#7D7C7D]/20 p-5 bg-[#232323]/80 backdrop-blur-md">
      <div
        style={{
          backgroundImage: `url('${workout.posterImgixUrl + "?w=300"}')`,
          // backfeo
        }}
        className={`h-[26rem] sm:h-[32rem] flex flex-col shadow-md bg-center items-start justify-between w-full relative bg-cover rounded-xl overflow-hidden`}
      >
        <div className="absolute inset-x-0 top-0 h-3/5 bg-gradient-to-b from-black/60 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/90 to-transparent" />
        <div className="flex items-center justify-between px-4 pt-4">
          <div className="relative flex items-center gap-2">
            {team?.coaches?.map?.(({ profileImg }, i) => {
              return (
                <Avatar
                  key={i}
                  src={profileImg}
                  variant={Avatar.variant.Circle}
                  size={Avatar.size.XS}
                  className={clsx({
                    "absolute -ml-10 shadow rounded-full": i > 0,
                  })}
                />
              );
            })}
            <div className="flex flex-col justify-center">
              <h4 className="text-lg font-medium leading-none">
                {team?.name || "..."}
              </h4>
              <div className="flex gap-1 text-sm">
                <span className="">Coach</span>
                <span>
                  {team?.coaches
                    ?.map?.(({ givenName, profileImg }, i) => givenName)
                    ?.join?.(", ")}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="relative flex p-4 space-x-2 divide-x divide-white">
          <div className="text-center">
            <h2 className="text-xl font-bold">
              {data?.duration ? dateFns.secondsToMinutes(data.duration) : "..."}
            </h2>
            <p className="text-sm">Min</p>
          </div>
          <div className="pl-2">
            <h1 className="text-xl font-bold">{workout.name}</h1>
            <div className="flex items-center space-x-2">
              <p className="text-sm">{workout.type}</p>
              <ReactStars
                count={5}
                value={Number(data?.rating?.avg)}
                edit={false}
                size={12}
                activeColor="#E6FF00"
                key={data?.rating?.avg}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="pt-4">
        {countdown ? (
          <div className="flex items-end justify-between">
            <h4 className="text-base font-bold text-transparent bg-white bg-clip-text">
              ENDS IN
            </h4>
            <Countdown enabled={countdown} />
          </div>
        ) : null}
        {data?.playlist ? <Playlist data={data?.playlist} /> : null}
      </div>
    </div>
  );
});

function Loading() {
  return (
    <motion.div className="h-screen text-white bg-black center">
      <img
        className=""
        src="/assets/logo/white.svg"
        alt="Ladder Logo"
        width={632 * 0.15}
        height={104 * 0.15}
      />
    </motion.div>
  );
}

const LoadingSpinner = () => (
  <svg
    className="w-6 h-6 text-black animate-spin"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

const CTAEmailSection = ({
  handleSubmitCTA,
  loading,
  value,
  setValue,
  handleKeyDown,
  handleSubmit,
  fromEmail,
}) => {
  const { query } = useRouter();

  return (
    <div
      className={clsx("relative center", {
        "px-6 py-4 border rounded-full border-white/60": !fromEmail,
      })}
    >
      {fromEmail ? (
        <button
          onClick={async () => await handleSubmitCTA()}
          className="w-full px-4 py-3 mx-auto text-sm text-black rounded-full md:max-w-md center right-2 inset-y-2 bg-volt cta lg:text-base"
        >
          {loading ? <LoadingSpinner /> : <span>START YOUR WORKOUT</span>}
        </button>
      ) : (
        <>
          <input
            autoFocus
            value={value}
            placeholder={"Enter Your Email"}
            name="email"
            type="email"
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={handleKeyDown}
            className="relative w-full pr-24 mx-auto text-base text-left text-white bg-transparent outline-none lg:pr-32 focus:shadow-none ring-transparent focus:ring-transparent focus:outline-none flex-center"
          />
          <button
            onClick={async () => await handleSubmit(value)}
            className="absolute px-4 py-2 text-sm text-black rounded-full center right-2 inset-y-2 bg-volt cta lg:text-base"
          >
            {loading ? <LoadingSpinner /> : <span> GET FREE WORKOUTS</span>}
          </button>
        </>
      )}
    </div>
  );
};

function Page({ workout, yWorkout, tWorkout, teamID, error }) {
  const { push, query, isReady } = useRouter();
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const { observe, inView } = useInView({});

  const fromEmail = query.utm_source === "email" || query.from === "email";
  const isDisabled = !value.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i);

  const handleSubmit = async (value) => {
    if (isDisabled) return;
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? "animate-enter" : "animate-leave"
          } max-w-md w-full bg-neutral-800 shadow-lg rounded-lg pointer-events-auto flex border border-neutral-700`}
        >
          <div className="justify-start flex-1 w-0 p-4 center">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <CheckCircledIcon className="w-6 h-6 text-volt" />
              </div>
              <div className="flex-1 ml-3">
                <p className="text-sm font-medium text-neutral-100">
                  Success! You will receive Daily Workouts from Ladder in your
                  email
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
      {
        position: "top-center",
        duration: 3000,
        id: "email-capture",
      }
    );

    setLoading(true);
    identify({ email: value });
    captureEvent({
      category: "todays_workout",
      action: "Today_WO_Email_Captured",
      label: "Email Capture",
      value,
    });

    Cookies.set(WO_EMAIL_CAPTURE_COOKIE_NAME, value);

    await push(
      getUrlWithUtms(
        new URL(
          `${process.env.NEXT_PUBLIC_DOMAIN}/workout?workoutId=${workout.id}`
        )
      )
    );
    setLoading(false);
  };
  const handleSubmitCTA = async () => {
    setLoading(true);

    captureEvent({
      category: "todays_workout",
      action: "Today_WO_CTA_Clicked",
      label: "CTA Clicked",
    });

    await push(
      getUrlWithUtms(
        new URL(
          `${process.env.NEXT_PUBLIC_DOMAIN}/workout?workoutId=${workout.id}&hideEmailCapture=1`
        )
      )
    );
    setLoading(false);
  };

  const handleKeyDown = async ({ key }) => {
    if (isDisabled) return;
    if (key === "Enter") {
      return await handleSubmit(value);
    }
  };

  if (!workout || !isReady) return <Loading />;

  return (
    <>
      <HeadContent title="Ladder | Workout of the Day" />
      <Toaster
        position="top-center"
        toastOptions={{
          // Define default options
          className: "",
          duration: 5000,
        }}
      />
      <div className="w-full max-w-full min-h-screen overflow-hidden text-white">
        <div className="container flex-col px-2 pt-6 pb-4 mx-auto text-center lg:py-6 center">
          <a
            className="self-start"
            href={getUrlWithUtms(new URL(process.env.NEXT_PUBLIC_DOMAIN))}
          >
            <img
              className="lg:w-[8rem] w-[8rem] px-2"
              src="/assets/logo/white.svg"
              alt="Ladder Logo"
            />
          </a>
          {fromEmail ? (
            <>
              <h3 className="mt-6 text-xl tracking-tight lg:mt-8 lg:tracking-normal lg:text-3xl">
                LADDER&apos;S <span className="text-volt">FREE</span>
              </h3>
              <h1 className="text-3xl tracking-tight lg:mt-1 lg:tracking-normal lg:text-5xl heading">
                WORKOUT OF THE DAY
              </h1>
              <p className="max-w-xl px-2 mt-4 mb-2 text-sm text-center lg:px-12 lg:mt-5 lg:text-base opacity-60">
                Today&apos;s Workout Ends In
              </p>
              <div className="center opacity-90">
                <Countdown enabled={!loading} size={"lg"} />
              </div>
            </>
          ) : (
            <>
              <h3 className="mt-6 text-xl tracking-tight lg:mt-8 lg:tracking-normal lg:text-3xl">
                GET LADDER&apos;S <span className="text-volt">FREE</span>
              </h3>
              <h1 className="text-3xl tracking-tight lg:mt-1 lg:tracking-normal lg:text-5xl heading">
                WORKOUT OF THE DAY
              </h1>
              <h1 className="text-xl tracking-tight lg:mt-1 lg:tracking-normal lg:text-3xl">
                IN YOUR INBOX, EVERY MORNING
              </h1>
              <p className="max-w-xl px-2 mt-4 text-sm text-center lg:px-12 lg:mt-5 lg:text-base opacity-60">
                Get a free strength and conditioning workout every day. Used by
                40k+ people to get stronger, mentally and physically.
              </p>
            </>
          )}
          {!fromEmail ? (
            <div ref={observe} className="w-full max-w-xl px-2 mx-auto mt-6">
              <CTAEmailSection
                {...{
                  handleSubmitCTA,
                  loading,
                  value,
                  setValue,
                  handleKeyDown,
                  handleSubmit,
                  fromEmail,
                  hideFromEmail: true,
                }}
              />
            </div>
          ) : null}
          {!fromEmail ? (
            <div className="relative flex mt-6 mb-2 space-x-2 center">
              <div className="relative flex -space-x-3">
                {[1, 2, 3, 4].map?.((img, i) => {
                  return (
                    <Avatar
                      key={i}
                      src={`/assets/avatars/${img}.png`}
                      variant={Avatar.variant.Circle}
                      size={Avatar.size.XS}
                      className={clsx({
                        "transform shadow rounded-full": i > 0,
                      })}
                    />
                  );
                })}
              </div>
              <span className="flex text-sm text-white/70">
                <b className="mr-1 font-bold text-volt">466,904</b> workouts
                completed
              </span>
            </div>
          ) : null}
        </div>
        <div className="relative mb-12">
          <motion.div
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={{ x: 250, y: 48, opacity: 0.5 }}
            transition={{ delay: 0.3 }}
            className="absolute inset-0 blur-[6px]"
          >
            <Card workout={yWorkout || workout} teamID={teamID} />
          </motion.div>
          <motion.div
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={{ x: -250, y: 48, opacity: 0.5 }}
            transition={{ delay: 0.3 }}
            className="absolute inset-0 blur-[6px]"
          >
            <Card workout={tWorkout || workout} teamID={teamID} />
          </motion.div>
          <div className="px-6 lg:px-0">
            <Card
              workout={workout}
              teamID={teamID}
              countdown={!fromEmail || !loading}
            />
          </div>
        </div>
        <motion.div
          initial={fromEmail ? "inView" : "outView"}
          animate={!inView || fromEmail ? "inView" : "outView"}
          variants={{
            inView: { opacity: 1 },
            outView: { opacity: 0 },
          }}
          style={{
            background:
              "linear-gradient(180deg, rgba(14, 14, 14, 0) 0%, #0E0E0E 34.27%)",
          }}
          className="fixed z-10 inset-x-0 bottom-0 h-[10rem] lg:h-[12rem] bg-gradient-to-t from-black to-transparent"
        />
        <motion.div
          initial={fromEmail ? "inView" : "outView"}
          animate={!inView || fromEmail ? "inView" : "outView"}
          variants={{
            inView: { y: 0 },
            outView: { y: 120 },
          }}
          className="fixed inset-x-0 bottom-0 z-10 w-full max-w-xl px-4 py-4 mx-auto lg:py-8"
        >
          <CTAEmailSection
            {...{
              handleSubmitCTA,
              loading,
              value,
              setValue,
              handleKeyDown,
              handleSubmit,
              fromEmail,
            }}
          />
        </motion.div>
      </div>
      <Footer />
    </>
  );
}

const PageWrapper = (props) => {
  return (
    <ExperimentProvider>
      <Page {...props} />
    </ExperimentProvider>
  );
};

const DEFAULT_TEAM_ID = "8886e69b-569a-4907-b9ad-b8b1cadf5adf";

export const getWeekData = () => {
  const startDate = dateFnsTZ.format(
    dateFns.startOfDay(new Date()),
    "yyyy-MM-dd",
    {
      timeZone: "America/Chicago",
    }
  );

  const endDate = dateFnsTZ.format(dateFns.endOfDay(new Date()), "yyyy-MM-dd", {
    timeZone: "America/Chicago",
  });

  return { startDate, endDate };
};

const handleGetWorkout = async (workoutID, teamID) => {
  let manualWorkout;

  if (workoutID) {
    const getWorkout = getWorkoutByID(workoutID);
    manualWorkout = await getWorkout();
  }

  const getThisWeeksProgramCalendar = getProgramCalanderByDateRange({
    programID: teamID,
    ...getWeekData(),
  });
  const { workouts } = await getThisWeeksProgramCalendar();

  const todayKey = dateFnsTZ.format(new Date(), "yyyy-MM-dd");
  const endOfWeekKey = dateFnsTZ.format(
    dateFns.endOfWeek(new Date(), { weekStartsOn: 0 }),
    "yyyy-MM-dd"
  );
  const startOfWeekKey = dateFnsTZ.format(
    dateFns.endOfWeek(new Date(), { weekStartsOn: 1 }),
    "yyyy-MM-dd"
  );

  const workout =
    manualWorkout ||
    workouts[todayKey] ||
    workouts[endOfWeekKey] ||
    workouts[startOfWeekKey] ||
    null;
  const yWorkout =
    workouts[endOfWeekKey] ||
    workouts[startOfWeekKey] ||
    workouts[todayKey] ||
    null;
  const tWorkout =
    workouts[endOfWeekKey] ||
    workouts[startOfWeekKey] ||
    workouts[todayKey] ||
    null;

  return { workout, yWorkout, tWorkout };
};

export async function getStaticProps() {
  try {
    const root = isLocal
      ? "http://localhost:3000"
      : process.env.NEXT_PUBLIC_DOMAIN;
    const { teamData } = await fetcher(root + "/api/notion/workout-of-the-day");

    const teamID = teamData?.teamID || DEFAULT_TEAM_ID;
    const workoutID = teamData?.workoutID;

    const { workout, yWorkout, tWorkout } = await handleGetWorkout(
      workoutID,
      teamID
    );

    return {
      props: {
        workout,
        yWorkout,
        tWorkout,
        teamID,
      },
      revalidate: 60 * 60 * 5, // In seconds
    };
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
    return {
      props: {
        error: JSON.stringify(error),
        teamID: DEFAULT_TEAM_ID,
      },
    };
  }
}

PageWrapper.Layout = Layout;
export default PageWrapper;
